import React, { useEffect, useMemo, useState } from 'react';

import { CircularProgress, Tooltip, useMediaQuery } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import clsx from 'clsx';
import { isNil } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Filters from 'components/Filters';
import instances from 'config/constants/instances';
import DIRECTIONS from 'config/constants/sortDirections';
import resolveDirection from 'helpers/resolveDirection';
import search_messages from 'messages/search_messages';
import table_messages from 'messages/table_messages';

import InputWithDebouncedAction from '../InputWithDebouncedAction/InputWithDebouncedAction';

import useStyles from './Table.styles';

const getSortIcon = (direction, className) => {
  switch (direction) {
    case DIRECTIONS.ASC:
      return <KeyboardArrowDownIcon className={className} />;
    case DIRECTIONS.DESC:
      return <KeyboardArrowUpIcon className={className} />;
    default:
      return null;
  }
};

const interactiveSelector = 'a, button, input, select, textarea, [role="button"]:not(td):not(dd), [role="link"]:not(td):not(dd)';

const shouldIgnoreRowActivation = event => event.target.closest(interactiveSelector) && event.target !== event.currentTarget;

const Table = ({
  data: dataFromProps,
  dataMapping,
  filterInLastColumn,
  filtersService,
  onRowClick,
  sort,
  onSort,
  hideThead,
  fixedHeader,
  getRowHref,
  clickableRow,
  refetching,
  enableSearch,
  onSearch,
  searchValue,
  searchAdornment,
  openInNewTab,
  disableClickAction,
  isRowSelected,
  keyGenerator,
  noDataMessage,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const isPivoted = useMediaQuery('@media screen and (max-width: 55em)');
  const styles = useStyles({ fixedHeader, refetching, isPivoted });

  const [data, setData] = useState(null);
  const [tableId] = useState(() => `table-${Math.random().toString(36).slice(2)}`);
  const columnCount = dataMapping.filter(({ isAddon }) => !isAddon).length + (filtersService && !filterInLastColumn ? 1 : 0);
  const visibleColumns = useMemo(() => dataMapping.filter(({ isAddon }) => !isAddon), [dataMapping]);
  useEffect(() => {
    if (dataFromProps) setData(dataFromProps);
  }, [dataFromProps]);
  const noData = data && !data.length;

  const initiateSort = (id, customSort) => {
    if (customSort) {
      return customSort(id);
    }
    return onSort(id, resolveDirection(sort?.direction, id === sort?.key));
  };
  const onThClick = (event, { id, customSort }) => {
    initiateSort(id, customSort);
  };
  const onThKeyDown = (event, { id, customSort }) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    initiateSort(id, customSort);
  };

  const rowClickHandler = (event, row) => {
    if (shouldIgnoreRowActivation(event)) return;
    if (disableClickAction && disableClickAction(row)) return;
    if (onRowClick) onRowClick(event, row);
    if (getRowHref && clickableRow) {
      const href = getRowHref(row);
      if (href) {
        if (openInNewTab) {
          window.open(href, '_blank');
        } else {
          history.push(href);
        }
      }
    }
  };

  const onCellKeyDown = (event, row) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    rowClickHandler(event, row);
  };

  const renderPivotValueContent = (displayContent, row, isInteractiveRow, interactiveRole) => {
    if (!isInteractiveRow) {
      return <span className={styles.tdContent}>{displayContent}</span>;
    }

    if (interactiveRole === 'link') {
      return (
        <span
          className={clsx(styles.tdContent, styles.pivotedValueAction)}
          onKeyDown={event => onCellKeyDown(event, row)}
          role='link'
          tabIndex={0}
        >
          {displayContent}
        </span>
      );
    }

    return (
      <span
        className={clsx(styles.tdContent, styles.pivotedValueAction)}
        onKeyDown={event => onCellKeyDown(event, row)}
        role='button'
        tabIndex={0}
      >
        {displayContent}
      </span>
    );
  };

  useEffect(() => {
    if (process.env.REACT_APP_INSTANCE !== instances.PRODUCTION) {
      const colsSum = dataMapping.reduce((acc, curr) => acc + parseInt(curr.width || 0, 10), 0);
      // eslint-disable-next-line no-console
      if (colsSum > 100) console.error(`Sum of columns width should be less than or equal 100, now is ${colsSum}`);
      if (colsSum < 100 && !dataMapping.some(({ width }) => !width))
        // eslint-disable-next-line no-console
        console.error(`Width of all columns are set, sum of it should equal 100, now is ${colsSum}`);
    }
  }, [dataMapping]);

  return (
    <div className={styles.tableWrapper}>
      {filtersService && isPivoted && (
        <div className={styles.filtersButton}>
          <Filters {...filtersService} />
        </div>
      )}
      <table className={styles.table}>
        {!hideThead && (
          <thead className={styles.thead}>
            {enableSearch && (
              <tr className={styles.searchBar}>
                <th className={clsx(styles.th, styles.thSearch)} colSpan='100%'>
                  <div className={styles.searchRowContent}>
                    <div className={styles.searchInputWrapper}>
                      <InputWithDebouncedAction
                        action={onSearch}
                        formControl={{ size: 'small' }}
                        label={t(search_messages.input_label)}
                        value={searchValue}
                      />
                    </div>
                    {searchAdornment && <div className={styles.searchAdornmentWrapper}>{searchAdornment}</div>}
                  </div>
                </th>
              </tr>
            )}
            {!isPivoted && (
              <tr>
                {dataMapping.map(({ label, id, customSort, blockSorting, width, isAddon }, colIndex) => {
                  const isSorted = !blockSorting && sort?.key === id && sort?.direction;
                  const isLastColWithFilter = filtersService && filterInLastColumn && colIndex === dataMapping.length - 1;
                  const handleThClick = isLastColWithFilter || blockSorting ? undefined : event => onThClick(event, { id, customSort });
                  const handleThKeyDown = isLastColWithFilter || blockSorting ? undefined : event => onThKeyDown(event, { id, customSort });
                  const isSortable = !isLastColWithFilter && !blockSorting;
                  let ariaSort;
                  if (isSorted && sort.direction === DIRECTIONS.ASC) {
                    ariaSort = 'ascending';
                  } else if (isSorted && sort.direction === DIRECTIONS.DESC) {
                    ariaSort = 'descending';
                  }
                  if (isAddon) {
                    return (
                      <th
                        key={`${label}-${id}`}
                        aria-hidden='true'
                        className={clsx(styles.th, styles.addonHeaderCell)}
                        scope='col'
                        width={isPivoted ? undefined : width}
                      />
                    );
                  }
                  return (
                    <th
                      key={`${label}-${id}`}
                      aria-sort={ariaSort}
                      className={clsx(
                        styles.th,
                        isLastColWithFilter && styles.filterHeaderCell,
                        !isLastColWithFilter && isSorted && styles.sort,
                        !isLastColWithFilter && blockSorting && styles.blockSorting,
                      )}
                      id={`${tableId}-${id}`}
                      onClick={handleThClick}
                      onKeyDown={handleThKeyDown}
                      scope='col'
                      tabIndex={isSortable ? 0 : undefined}
                      width={isPivoted ? undefined : width}
                    >
                      {isLastColWithFilter ? (
                        <Filters {...filtersService} />
                      ) : (
                        <div className={styles.thContent}>
                          <span>{label}</span>
                          {isSorted && sort?.additionalInfo && <span className={styles.additionalInfo}>{sort.additionalInfo}</span>}
                          <span className={styles.sortIconWrapper}>{getSortIcon(isSorted && sort.direction, styles.sortIcon)}</span>
                        </div>
                      )}
                    </th>
                  );
                })}
                {filtersService && !filterInLastColumn && !isPivoted && (
                  <th className={clsx(styles.th, styles.filterHeaderCell)}>
                    <Filters {...filtersService} />
                  </th>
                )}
              </tr>
            )}
          </thead>
        )}
        <tbody>
          {data &&
            data.map((row, index) => {
              const clickDisabled = disableClickAction && disableClickAction(row);
              const isInteractiveRow = (!!onRowClick || clickableRow) && !clickDisabled;
              const interactiveRole = getRowHref && clickableRow && getRowHref(row) ? 'link' : 'button';
              return (
                <tr
                  key={keyGenerator ? keyGenerator(row, index) : `${row.id}-${index}`}
                  className={clsx(
                    styles.tr,
                    isInteractiveRow && styles.clickableTr,
                    isRowSelected && isRowSelected(row) && styles.selectedRow,
                  )}
                  onClick={event => rowClickHandler(event, row)}
                >
                  {isPivoted ? (
                    <td className={styles.pivotedTd} colSpan={columnCount}>
                      <dl className={styles.pivotedRow}>
                        {visibleColumns.map(({ get, label, id }, tdIndex) => {
                          const cellContent = get(row);
                          const displayContent = isNil(cellContent) ? t(...table_messages.no_data_cell) : cellContent;
                          return (
                            <div key={`${row.id}-${id || tdIndex}`} className={styles.pivotedItem}>
                              <dt className={styles.pivotedLabel}>{label}:</dt>
                              <dd className={styles.pivotedValue}>
                                {renderPivotValueContent(displayContent, row, isInteractiveRow, interactiveRole)}
                              </dd>
                            </div>
                          );
                        })}
                        {dataMapping
                          .filter(({ isAddon }) => isAddon)
                          .map(({ get, id }, tdIndex) => {
                            const cellContent = get(row);
                            return (
                              <div key={`${row.id}-${id || tdIndex}`} className={styles.addonWrapper}>
                                <span className={styles.tdContent}>
                                  {isNil(cellContent) ? t(...table_messages.no_data_cell) : cellContent}
                                </span>
                              </div>
                            );
                          })}
                      </dl>
                    </td>
                  ) : (
                    dataMapping.map(({ get, width, isAddon, id }, tdIndex) => {
                      const cellContent = get(row);
                      return (
                        <Tooltip key={`${row.id}-${tdIndex}`} enterDelay={700} title={clickDisabled || ''}>
                          <td
                            className={clsx(styles.td, isInteractiveRow && !isAddon && styles.focusableTd, isAddon && styles.addonTd)}
                            headers={!isAddon ? `${tableId}-${id}` : undefined}
                            onKeyDown={isInteractiveRow && !isAddon ? event => onCellKeyDown(event, row) : undefined}
                            role={isInteractiveRow && !isAddon ? interactiveRole : undefined}
                            tabIndex={isInteractiveRow && !isAddon ? 0 : undefined}
                            width={width}
                          >
                            <span className={styles.tdContent}>{isNil(cellContent) ? t(...table_messages.no_data_cell) : cellContent}</span>
                          </td>
                        </Tooltip>
                      );
                    })
                  )}
                  {filtersService && !filterInLastColumn && !isPivoted && <td className={styles.td} />}
                </tr>
              );
            })}
        </tbody>
      </table>
      {noData ? <div className={styles.noData}>{noDataMessage || t(...table_messages.no_data_table)}</div> : null}
      {refetching && <CircularProgress className={styles.loader} color='primary' size={60} />}
    </div>
  );
};

const FiltersPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }),
);

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  onRowClick: PropTypes.func,
  dataMapping: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.string, get: PropTypes.func, customSort: PropTypes.func }),
  ).isRequired,
  filterInLastColumn: PropTypes.bool,
  filtersService: PropTypes.shape({
    availableFilters: PropTypes.objectOf(
      PropTypes.shape({
        sectionName: PropTypes.arrayOf(PropTypes.string),
        filters: FiltersPropTypes,
      }),
    ),
    filters: PropTypes.objectOf(PropTypes.array),
    setFilters: PropTypes.func,
  }),
  sort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf([DIRECTIONS.DESC, DIRECTIONS.ASC]),
    additionalInfo: PropTypes.string,
  }),
  onSort: PropTypes.func,
  hideThead: PropTypes.bool,
  fixedHeader: PropTypes.bool,
  getRowHref: PropTypes.func,
  clickableRow: PropTypes.bool,
  enableSearch: PropTypes.bool,
  onSearch: PropTypes.func,
  searchValue: PropTypes.string,
  searchAdornment: PropTypes.node,
  openInNewTab: PropTypes.bool,
  disableClickAction: PropTypes.func,
  isRowSelected: PropTypes.func,
  keyGenerator: PropTypes.func,
  noDataMessage: PropTypes.node,
};

Table.defaultProps = {
  data: null,
  filterInLastColumn: false,
  filtersService: null,
  onRowClick: null,
  sort: {},
  onSort: () => {},
  onSearch: () => {},
  searchValue: undefined,
  searchAdornment: null,
  hideThead: false,
  fixedHeader: false,
  getRowHref: null,
  clickableRow: false,
  enableSearch: false,
  disableClickAction: null,
  isRowSelected: null,
  keyGenerator: null,
  noDataMessage: null,
  openInNewTab: false,
};

export default Table;
